<template>
  <div id="app">
    <div v-if="isLanguageChanging" class="fixed inset-0 z-[9999] flex items-center justify-center">
      <Loader size="large" />
    </div>
    <div v-if="isNavigating && !isLanguageChanging" class="fixed inset-0 z-[9999] flex items-center justify-center">
      <Loader size="large" />
    </div>
    <!-- Text Banners (not sticky - in normal flow) -->
    <div ref="textBannersWrapper" class="w-full">
      <Banner v-for="(banner, index) in topBanners" :key="`text-top-${banner.id}`" :banner-text="banner.text"
        :banner-subtext="banner.subtext" :background-color="banner.background_color" :text-color="banner.text_color"
        :links="banner.links || []" :dismissible="false" :index="index" />
    </div>
    <!-- Navbar - sticky at top -->
    <div ref="navbarWrapper" class="navbar-wrapper sticky top-0 left-0 right-0 z-40"
      :class="{ 'is-media-page': currentPage === 'reader' || currentPage === 'player' }">
      <Navbar @open-search="showSearchBar = true" />
    </div>
    <BannerPreview v-if="currentPage === 'admin' && adminBannerPreview" :banner="adminBannerPreview" />
    <SearchBar :visible="showSearchBar" @close="showSearchBar = false" @search="handleSearch" />
    <div class="page-content-wrapper" :class="[
      (isLanguageChanging || isNavigating) ? 'opacity-0 pointer-events-none' : ''
    ]" style="transition: opacity 0.2s;">
      <CategoriesBar :visible="showCategoriesBar" :categories="categories" :permanent="currentPage === 'books'"
        @close="showCategoriesBar = false" />
      <PromotionalBanner v-for="banner in topImageBanners" :key="`image-top-${banner.id}`" :banner="banner" />
      <div v-if="!isPageReady" class="flex items-center justify-center min-h-[60vh]">
        <Loader size="large" />
      </div>
      <ApiUnavailablePage v-else-if="!isApiAvailable" />
      <template v-else>
        <LandingPage v-if="currentPage === 'home'" />
        <BooksPage v-else-if="currentPage === 'books'" />
        <BookProductPage v-else-if="currentPage === 'book'" />
        <AuthorsPage v-else-if="currentPage === 'authors'" />
        <AuthorPage v-else-if="currentPage === 'author'" />
        <AboutPage v-else-if="currentPage === 'about'" />
        <TeamPage v-else-if="currentPage === 'team'" />
        <PartnersPage v-else-if="currentPage === 'partners'" />
        <ProductsPage v-else-if="currentPage === 'products'" />
        <FAQPage v-else-if="currentPage === 'faq'" />
        <GuidelinesPage v-else-if="currentPage === 'guidelines'" />
        <TermsPage v-else-if="currentPage === 'terms'" />
        <PrivacyPage v-else-if="currentPage === 'privacy'" />
        <CookiesPage v-else-if="currentPage === 'cookies'" />
        <ContactPage v-else-if="currentPage === 'contact'" />
        <DonatePage v-else-if="currentPage === 'donate'" />
        <EventDetailPage v-else-if="currentPage === 'event-detail'" :event-id="eventId" />
        <EventsPage v-else-if="currentPage === 'events'" />
        <CareersPage v-else-if="currentPage === 'careers'" />
        <CareerDetailPage v-else-if="currentPage === 'career-detail'" :career-id="careerDetailId" />
        <CareerApplicationPage v-else-if="currentPage === 'career-application'" :career-id="careerApplicationId" />
        <PressPage v-else-if="currentPage === 'press'" />
        <SubmissionsPage v-else-if="currentPage === 'submissions'" />
        <LoginPage v-else-if="currentPage === 'login'" />
        <RegisterPage v-else-if="currentPage === 'register'" />
        <ResetPasswordPage v-else-if="currentPage === 'reset-password'" />
        <MessagePage v-else-if="currentPage === 'message'" />
        <PurchaseHistoryPage v-else-if="currentPage === 'profile' || currentPage === 'purchase-history'" />
        <MyLibraryPage v-else-if="currentPage === 'my-library'" />
        <EbookReader v-else-if="currentPage === 'reader'" :id="mediaId" :mode="mediaMode" />
        <AudiobookPlayer v-else-if="currentPage === 'player'" :id="mediaId" :mode="mediaMode" />
        <SettingsPage v-else-if="currentPage === 'settings'" />
        <CartPage v-else-if="currentPage === 'cart'" />
        <CheckoutAuthPage v-else-if="currentPage === 'checkout-auth'" />
        <CheckoutPage v-else-if="currentPage === 'checkout'" />
        <CheckoutSuccessPage v-else-if="currentPage === 'checkout-success'" />
        <DonateSuccessPage v-else-if="currentPage === 'donate-success'" />
        <AdminDashboard v-else-if="currentPage === 'admin'" :admin-banner-preview="adminBannerPreview"
          :initial-section="adminSection" @banner-preview-updated="updateAdminBannerPreview" />
        <NewsletterConfirmPage v-else-if="currentPage === 'newsletter-confirm'" />
        <NewsletterUnsubscribePage v-else-if="currentPage === 'newsletter-unsubscribe'" />
        <TestOrdersPage v-else-if="currentPage === 'test-orders'" />
      </template>
    </div>

    <template v-if="isPageReady">
      <PromotionalBanner v-for="banner in bottomImageBanners" :key="`image-bottom-${banner.id}`" :banner="banner" />
      <Banner v-for="banner in bottomBanners" :key="`text-bottom-${banner.id}`" :banner-text="banner.text"
        :banner-subtext="banner.subtext" :background-color="banner.background_color" :text-color="banner.text_color"
        :links="banner.links || []" :dismissible="false" />

      <NewsletterSection v-if="currentPage === 'home'" />

      <Footer
        v-if="currentPage !== 'admin' && currentPage !== 'newsletter-confirm' && currentPage !== 'newsletter-unsubscribe'" />
    </template>

    <!-- Cookie Consent Banner -->
    <CookieConsent />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import Navbar from './components/Navbar.vue'
import SearchBar from './components/SearchBar.vue'
import CategoriesBar from './components/CategoriesBar.vue'
import PromotionalBanner from './components/PromotionalBanner.vue'
import CookieConsent from './components/CookieConsent.vue'
import LandingPage from './components/LandingPage.vue'
import BooksPage from './components/BooksPage.vue'
import BookProductPage from './components/BookProductPage.vue'
import AuthorsPage from './components/AuthorsPage.vue'
import AuthorPage from './components/AuthorPage.vue'
import AboutPage from './components/AboutPage.vue'
import PartnersPage from './components/PartnersPage.vue'
import ContactPage from './components/ContactPage.vue'
import DonatePage from './components/DonatePage.vue'
import EventsPage from './components/EventsPage.vue'
import EventDetailPage from './components/EventDetailPage.vue'
import CareersPage from './components/CareersPage.vue'
import CareerDetailPage from './components/CareerDetailPage.vue'
import CareerApplicationPage from './components/CareerApplicationPage.vue'
import PressPage from './components/PressPage.vue'
import SubmissionsPage from './components/SubmissionsPage.vue'
import TeamPage from './components/TeamPage.vue'
import FAQPage from './components/FAQPage.vue'
import TermsPage from './components/TermsPage.vue'
import PrivacyPage from './components/PrivacyPage.vue'
import CookiesPage from './components/CookiesPage.vue'
import ProductsPage from './components/ProductsPage.vue'
import GuidelinesPage from './components/GuidelinesPage.vue'
import LoginPage from './components/LoginPage.vue'
import RegisterPage from './components/RegisterPage.vue'
import ResetPasswordPage from './components/ResetPasswordPage.vue'
import MessagePage from './components/MessagePage.vue'
import PurchaseHistoryPage from './components/PurchaseHistoryPage.vue'
import MyLibraryPage from './components/MyLibraryPage.vue'
import EbookReader from './components/EbookReader.vue'
import AudiobookPlayer from './components/AudiobookPlayer.vue'
import SettingsPage from './components/SettingsPage.vue'
import CartPage from './components/CartPage.vue'
import CheckoutPage from './components/CheckoutPage.vue'
import CheckoutAuthPage from './components/CheckoutAuthPage.vue'
import CheckoutSuccessPage from './components/CheckoutSuccessPage.vue'
import DonateSuccessPage from './components/DonateSuccessPage.vue'
import AdminDashboard from './components/AdminDashboard.vue'
import BannerPreview from './components/admin/BannerPreview.vue'
import Footer from './components/Footer.vue'
import NewsletterConfirmPage from './components/NewsletterConfirmPage.vue'
import NewsletterUnsubscribePage from './components/NewsletterUnsubscribePage.vue'
import NewsletterSection from './components/NewsletterSection.vue'
import Banner from './components/Banner.vue'
import Loader from './components/Loader.vue'
import ApiUnavailablePage from './components/ApiUnavailablePage.vue'
import TestOrdersPage from './components/TestOrdersPage.vue'
import { translationService } from './services/translation'
import { publicService } from './services/public'
import { useApiStatus } from './composables/useApiStatus.js'

const currentPage = ref('home')
const eventId = ref('')
const careerDetailId = ref('')
// In-app reader/player (vendor-restricted digital content).
const mediaId = ref('')
const mediaMode = ref('purchase') // 'purchase' | 'sample'
const careerApplicationId = ref('')
const topBanners = ref([])
const bottomBanners = ref([])
const topImageBanners = ref([])
const bottomImageBanners = ref([])
const isLanguageChanging = ref(false)
const isNavigating = ref(false)
const isPageReady = ref(true)
const adminBannerPreview = ref(null)
const adminSection = ref(null)
const showSearchBar = ref(false)
const showCategoriesBar = ref(false)
const categories = ref([])
const textBannersWrapper = ref(null)
const navbarWrapper = ref(null)

const updateAdminBannerPreview = (bannerData) => {
  adminBannerPreview.value = bannerData
}
const { isApiAvailable } = useApiStatus()

const handleSearch = (query) => {
  window.location.href = `/books?q=${encodeURIComponent(query)}`
}

const notifyURLChange = () => {
  window.dispatchEvent(new CustomEvent('app:url-change', {
    detail: {
      url: window.location.href
    }
  }))
}

const loadCategories = async () => {
  try {
    const response = await publicService.getCategories({ limit: 1000 })
    categories.value = response.categories || []
  } catch (error) {
    console.error('Failed to load categories:', error)
  }
}

// Automatically show categories bar when on books page
watch(currentPage, (newPage) => {
  showCategoriesBar.value = newPage === 'books'
}, { immediate: true })

// Add or remove admin-page class from body for admin pages
watch(currentPage, (newPage) => {
  if (newPage === 'admin') {
    document.body.classList.add('admin-page')
  } else {
    document.body.classList.remove('admin-page')
  }
}, { immediate: true })

const trackPageView = () => {
  const path = window.location.pathname
  if (path.startsWith('/admin')) {
    return
  }
  publicService.recordPageView(path)
}

const updatePage = () => {
  const path = window.location.pathname
  if (path.startsWith('/book/')) {
    currentPage.value = 'book'
  } else if (path === '/authors' || path === '/authors/') {
    currentPage.value = 'authors'
  } else if (path.startsWith('/author/')) {
    currentPage.value = 'author'
  } else if (path === '/books' || path === '/books/') {
    currentPage.value = 'books'
  } else if (path === '/about' || path === '/about/') {
    currentPage.value = 'about'
  } else if (path === '/team' || path === '/team/') {
    currentPage.value = 'team'
  } else if (path === '/partners' || path === '/partners/') {
    currentPage.value = 'partners'
  } else if (path === '/products' || path === '/products/') {
    currentPage.value = 'products'
  } else if (path === '/faq' || path === '/faq/') {
    currentPage.value = 'faq'
  } else if (path === '/guidelines' || path === '/guidelines/') {
    currentPage.value = 'guidelines'
  } else if (path === '/terms' || path === '/terms/') {
    currentPage.value = 'terms'
  } else if (path === '/privacy' || path === '/privacy/') {
    currentPage.value = 'privacy'
  } else if (path === '/cookies' || path === '/cookies/') {
    currentPage.value = 'cookies'
  } else if (path === '/contact' || path === '/contact/') {
    currentPage.value = 'contact'
  } else if (path === '/donate' || path === '/donate/') {
    currentPage.value = 'donate'
  } else if (path.startsWith('/events/') && path !== '/events' && path !== '/events/') {
    const match = path.match(/^\/events\/(.+)$/)
    if (match) {
      currentPage.value = 'event-detail'
      eventId.value = match[1]
    } else {
      currentPage.value = 'events'
    }
  } else if (path === '/events' || path === '/events/') {
    currentPage.value = 'events'
  } else if (path === '/careers' || path === '/careers/') {
    currentPage.value = 'careers'
  } else if (path.startsWith('/careers/') && path !== '/careers' && path !== '/careers/' && !path.startsWith('/careers/apply')) {
    const match = path.match(/^\/careers\/(.+)$/)
    if (match) {
      currentPage.value = 'career-detail'
      careerDetailId.value = match[1]
    } else {
      currentPage.value = 'careers'
    }
  } else if (path.startsWith('/careers/apply/') && path !== '/careers/apply' && path !== '/careers/apply/') {
    const match = path.match(/^\/careers\/apply\/(.+)$/)
    if (match) {
      currentPage.value = 'career-application'
      careerApplicationId.value = match[1]
    } else {
      currentPage.value = 'careers'
    }
  } else if (path === '/careers/apply' || path === '/careers/apply/') {
    currentPage.value = 'career-application'
    careerApplicationId.value = null
  } else if (path === '/press' || path === '/press/') {
    currentPage.value = 'press'
  } else if (path === '/submissions' || path === '/submissions/') {
    currentPage.value = 'submissions'
  } else if (path === '/login' || path === '/login/') {
    currentPage.value = 'login'
  } else if (path === '/register' || path === '/register/') {
    currentPage.value = 'register'
  } else if (path.startsWith('/reset-password') || path.startsWith('/reset-password/')) {
    currentPage.value = 'reset-password'
  } else if (path === '/message' || path === '/message/') {
    currentPage.value = 'message'
  } else if (path === '/profile' || path === '/profile/') {
    currentPage.value = 'profile'
  } else if (path === '/purchase-history' || path === '/purchase-history/') {
    currentPage.value = 'purchase-history'
  } else if (path === '/my-library' || path === '/my-library/') {
    currentPage.value = 'my-library'
  } else if (path.startsWith('/read/sample/')) {
    currentPage.value = 'reader'
    mediaMode.value = 'sample'
    mediaId.value = path.replace(/\/$/, '').split('/').pop()
  } else if (path.startsWith('/read/')) {
    currentPage.value = 'reader'
    mediaMode.value = 'purchase'
    mediaId.value = path.replace(/\/$/, '').split('/').pop()
  } else if (path.startsWith('/listen/sample/')) {
    currentPage.value = 'player'
    mediaMode.value = 'sample'
    mediaId.value = path.replace(/\/$/, '').split('/').pop()
  } else if (path.startsWith('/listen/')) {
    currentPage.value = 'player'
    mediaMode.value = 'purchase'
    mediaId.value = path.replace(/\/$/, '').split('/').pop()
  } else if (path === '/settings' || path === '/settings/') {
    currentPage.value = 'settings'
  } else if (path === '/cart' || path === '/cart/') {
    currentPage.value = 'cart'
  } else if (path === '/checkout-auth' || path === '/checkout-auth/') {
    currentPage.value = 'checkout-auth'
  } else if (path === '/checkout' || path === '/checkout/') {
    currentPage.value = 'checkout'
  } else if (path === '/checkout-success' || path === '/checkout-success/') {
    currentPage.value = 'checkout-success'
  } else if (path === '/donate-success' || path === '/donate-success/') {
    currentPage.value = 'donate-success'
  } else if (path.startsWith('/admin')) {
    currentPage.value = 'admin'
    const adminMatch = path.match(/^\/admin\/(.+)$/)
    if (adminMatch) {
      adminSection.value = adminMatch[1].replace(/\/$/, '')
    } else {
      adminSection.value = null
    }
  } else if (path === '/newsletter/confirm' || path === '/newsletter/confirm/') {
    currentPage.value = 'newsletter-confirm'
  } else if (path === '/newsletter/unsubscribe' || path === '/newsletter/unsubscribe/') {
    currentPage.value = 'newsletter-unsubscribe'
  } else if (path === '/test/orders' || path === '/test/orders/') {
    currentPage.value = 'test-orders'
  } else {
    currentPage.value = 'home'
  }
}

const scrollToTop = () => {
  // Use multiple methods to ensure scroll works on all devices including mobile
  window.scrollTo({ top: 0, behavior: 'smooth' })
  // Fallback for older mobile browsers
  if (window.scrollY !== 0) {
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }
}

const handleLinkClick = async (e) => {
  const link = e.target.closest('a[href^="/"]')
  if (link) {
    e.preventDefault()
    const href = link.getAttribute('href')
    const currentPath = window.location.pathname
    const hrefPath = href.split('?')[0]

    if (currentPath === hrefPath || currentPath === `${hrefPath}/` || `${currentPath}/` === hrefPath) {
      scrollToTop()
      return
    }

    const isBookPage = href.startsWith('/book/')
    const wasBookPage = window.location.pathname.startsWith('/book/')
    const isNavigatingBetweenBooks = isBookPage && wasBookPage

    window.history.pushState({}, '', href)
    scrollToTop()

    isNavigating.value = true
    isPageReady.value = true

    await nextTick()
    updatePage()
    notifyURLChange()

    if (isBookPage) {
      await nextTick()
      window.dispatchEvent(new Event('book-navigation'))

      if (isNavigatingBetweenBooks) {
        await loadBanners()
        await nextTick()
        await waitForDOMUpdate()
        await waitForPageDataLoad()

        const currentLang = translationService.getLanguage()
        if (currentLang !== 'en') {
          try {
            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Translation timeout')), 30000)
            })
            await Promise.race([translatePage(), timeoutPromise])
            await nextTick()
            await waitForDOMUpdate()
          } catch (error) {
            console.error('Translation error during book navigation:', error)
          }
        }

        await waitForDOMUpdate()
        isNavigating.value = false
      }
    }

    trackPageView()
  }
}

const translatePage = async () => {
  await nextTick()
  await waitForDOMUpdate()

  const app = document.getElementById('app')
  if (app) {
    try {
      await translationService.translateElement(app)
      await nextTick()
      await waitForDOMUpdate()
    } catch (error) {
      console.error('Error translating page:', error)
      throw error
    }
  }
}

const waitForDOMUpdate = () => {
  return new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve)
    })
  })
}

const waitForPageDataLoad = async () => {
  await nextTick()
  await waitForDOMUpdate()

  const checkForLoading = () => {
    const app = document.getElementById('app')
    if (!app) return true

    const pageContentWrapper = app.querySelector('.page-content-wrapper')
    if (!pageContentWrapper) return false

    const text = pageContentWrapper.textContent || ''

    const loadingTexts = [
      'Loading page',
      'Loading books',
      'Loading book',
      'Loading events',
      'Loading authors',
      'Loading author',
      'Searching',
      'Inapakia',
      'Verifying'
    ]

    const hasLoadingText = loadingTexts.some(loadingText => text.includes(loadingText))
    if (hasLoadingText) return true

    const loaders = pageContentWrapper.querySelectorAll('[class*="Loader"], [class*="loader"], [data-loading="true"]')
    const hasVisibleLoaders = Array.from(loaders).some(loader => {
      const style = window.getComputedStyle(loader)
      const parent = loader.parentElement
      const parentStyle = parent ? window.getComputedStyle(parent) : null

      if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) < 0.1) {
        return false
      }

      if (parentStyle && (parentStyle.display === 'none' || parentStyle.visibility === 'hidden')) {
        return false
      }

      return true
    })

    return hasVisibleLoaders
  }

  let stableCount = 0
  let lastContentLength = 0
  let lastContentHash = ''
  let consecutiveLoadingChecks = 0
  let hasRenderedContent = false
  let loadingWasActive = false

  for (let i = 0; i < 40; i++) {
    await nextTick()
    await waitForDOMUpdate()

    const app = document.getElementById('app')
    if (!app) continue

    const isLoading = checkForLoading()

    if (isLoading) {
      loadingWasActive = true
      stableCount = 0
      lastContentLength = 0
      lastContentHash = ''
      consecutiveLoadingChecks++
      if (consecutiveLoadingChecks > 25) {
        break
      }
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      continue
    }

    if (loadingWasActive && !isLoading) {
      await nextTick()
      await waitForDOMUpdate()
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      loadingWasActive = false
    }

    consecutiveLoadingChecks = 0

    const pageContentWrapper = app.querySelector('.page-content-wrapper')
    const currentContent = pageContentWrapper ? (pageContentWrapper.textContent || '') : ''
    const currentContentLength = currentContent.length
    const contentHash = currentContent.substring(0, 2000)

    if (currentContentLength > 300) {
      hasRenderedContent = true
    }

    if (currentContentLength === lastContentLength && contentHash === lastContentHash && currentContentLength > 300) {
      stableCount++
      if (stableCount >= 4 && hasRenderedContent) {
        break
      }
    } else {
      stableCount = 0
    }

    lastContentLength = currentContentLength
    lastContentHash = contentHash

    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
  }

  await nextTick()
  await waitForDOMUpdate()
  await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
}

const handleLanguageChange = async (event) => {
  if (isLanguageChanging.value) {
    return
  }

  const oldLanguage = event?.detail?.oldLanguage || translationService.getLanguage()
  const newLanguage = event?.detail?.language || translationService.getLanguage()

  if (oldLanguage === newLanguage) {
    return
  }

  isLanguageChanging.value = true

  try {
    await waitForPageDataLoad()

    const app = document.getElementById('app')
    if (app && oldLanguage !== 'en') {
      await translationService.revertToEnglish(app)
      await nextTick()
      await waitForDOMUpdate()
    }

    if (newLanguage !== 'en') {
      await translationService.preTranslateCommonStrings(newLanguage)
      await translatePage()
    }

    await nextTick()
    await waitForDOMUpdate()
  } catch (error) {
    console.error('Translation error:', error)
  } finally {
    isLanguageChanging.value = false
  }
}

const loadBanners = async () => {
  try {
    const page = currentPage.value
    const [topText, bottomText, topImage, bottomImage] = await Promise.all([
      publicService.getTextBanners('top', page),
      publicService.getTextBanners('bottom', page),
      publicService.getImageBanners('top', page),
      publicService.getImageBanners('bottom', page)
    ])

    topBanners.value = Array.isArray(topText) ? topText : []
    bottomBanners.value = Array.isArray(bottomText) ? bottomText : []
    topImageBanners.value = Array.isArray(topImage) ? topImage : []
    bottomImageBanners.value = Array.isArray(bottomImage) ? bottomImage : []
  } catch (error) {
    console.error('Failed to load banners:', error)
    topBanners.value = []
    bottomBanners.value = []
    topImageBanners.value = []
    bottomImageBanners.value = []
  }
}

watch(currentPage, async () => {
  // Close search bar when navigating to a new page
  showSearchBar.value = false

  if (isLanguageChanging.value) {
    await new Promise((resolve) => {
      const checkLanguageChange = setInterval(() => {
        if (!isLanguageChanging.value) {
          clearInterval(checkLanguageChange)
          resolve()
        }
      }, 100)
    })
  }

  isNavigating.value = true
  isPageReady.value = true

  scrollToTop()
  await loadBanners()
  await nextTick()
  await waitForDOMUpdate()

  await waitForPageDataLoad()

  const currentLang = translationService.getLanguage()
  if (currentLang !== 'en') {
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Translation timeout')), 30000)
      })

      await Promise.race([translatePage(), timeoutPromise])
      await nextTick()
      await waitForDOMUpdate()
    } catch (error) {
      console.error('Translation error during page navigation:', error)
    }
  }

  await waitForDOMUpdate()
  // Ensure we're at the top after everything loads
  scrollToTop()
  isNavigating.value = false
  trackPageView()
})

const handlePopState = async () => {
  isNavigating.value = true
  isPageReady.value = true
  await nextTick()
  updatePage()
  notifyURLChange()

  if (window.location.pathname.startsWith('/book/')) {
    window.dispatchEvent(new Event('book-navigation'))
  }

  scrollToTop()

  await loadBanners()
  await nextTick()
  await waitForDOMUpdate()
  await waitForPageDataLoad()

  const currentLang = translationService.getLanguage()
  if (currentLang !== 'en') {
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Translation timeout')), 30000)
      })
      await Promise.race([translatePage(), timeoutPromise])
      await nextTick()
      await waitForDOMUpdate()
    } catch (error) {
      console.error('Translation error during popstate:', error)
    }
  }

  await waitForDOMUpdate()
  // Ensure we're at the top after everything loads
  scrollToTop()
  isNavigating.value = false
  trackPageView()
}

// No need for dynamic padding calculation since text banners are now in normal flow
// Only the navbar is sticky, and content flows naturally below the banners

onMounted(async () => {
  isPageReady.value = true
  updatePage()
  notifyURLChange()
  trackPageView()
  scrollToTop()
  await loadBanners()
  await loadCategories()
  window.addEventListener('popstate', handlePopState)
  document.addEventListener('click', handleLinkClick)
  window.addEventListener('language-changed', handleLanguageChange)

  const currentLang = translationService.getLanguage()
  if (currentLang !== 'en') {
    await translationService.preTranslateCommonStrings(currentLang)
  }

  isNavigating.value = true
  await waitForPageDataLoad()

  if (currentLang !== 'en') {
    try {
      await translatePage()
    } catch (error) {
      console.error('Translation error on initial load:', error)
    }
  }
  // Ensure we're at the top after initial load completes
  scrollToTop()
  isNavigating.value = false
})

onUnmounted(() => {
  window.removeEventListener('popstate', handlePopState)
  document.removeEventListener('click', handleLinkClick)
  window.removeEventListener('language-changed', handleLanguageChange)
})
</script>

<style>
#app {
  min-height: 100vh;
}

.page-content-wrapper {
  min-height: calc(100vh - 200px);
}

/* Reader/player pages only: when the navbar auto-hides (translated off-screen),
   its sticky wrapper is left empty at the top of the viewport. Let pointer events
   pass through it so the reader/player toolbars stay clickable. */
html[data-global-navbar-hidden='true'] .navbar-wrapper.is-media-page {
  pointer-events: none;
}
</style>
